/**
 * Same-browser resume for MvM hex battles.
 *
 * `battleId` only ever reached `HexBattlePage` through router state, so closing the tab or
 * navigating away stranded the player in a battle whose turn timer kept running server-side.
 * The battle document is fully authoritative (positions, hp/sp, cooldowns, placed elementals,
 * turn order), so the only thing needed to get back in is the id — persisted here, per wallet,
 * scoped to this browser.
 */

const STORAGE_KEY = 'eclipsera.mvm.activeBattle.v1'

/** Stale entries are dropped rather than trusted; the server is still asked before restoring. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000

export type StoredMvMBattle = {
  battleId: string
  /** Lower-cased owner wallet — an entry never restores into a different account. */
  wallet: string
  mapType: string | null
  savedAt: number
}

const normWallet = (w: string | null | undefined) =>
  String(w || '')
    .toLowerCase()
    .trim()

function readEntry(): StoredMvMBattle | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredMvMBattle> | null
    const battleId = String(parsed?.battleId || '').trim()
    const wallet = normWallet(parsed?.wallet)
    const savedAt = Number(parsed?.savedAt) || 0
    if (!battleId || !wallet) return null
    return { battleId, wallet, mapType: parsed?.mapType ?? null, savedAt }
  } catch {
    // Private-mode / disabled storage: resume is a convenience, never a hard dependency.
    return null
  }
}

/** Records the battle the player is currently in, so this browser can walk back into it. */
export function saveActiveMvMBattle(
  battleId: string | null | undefined,
  walletAddress: string | null | undefined,
  mapType: string | null = null,
): void {
  const id = String(battleId || '').trim()
  const wallet = normWallet(walletAddress)
  if (!id || !wallet || typeof window === 'undefined') return
  const prior = readEntry()
  // Same battle, same wallet, same map → nothing changed; skip the write so we don't churn
  // `savedAt` (and the storage event) on every re-render of the battle page.
  if (
    prior &&
    prior.battleId === id &&
    prior.wallet === wallet &&
    (prior.mapType ?? null) === (mapType ?? null)
  ) {
    return
  }
  try {
    const entry: StoredMvMBattle = {
      battleId: id,
      wallet,
      mapType: mapType ?? null,
      savedAt: Date.now(),
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch {
    /* storage full or unavailable — resume simply won't be offered */
  }
}

/** Returns the stored battle when it belongs to `walletAddress` and is still fresh. */
export function loadActiveMvMBattle(
  walletAddress: string | null | undefined,
): StoredMvMBattle | null {
  const wallet = normWallet(walletAddress)
  if (!wallet) return null
  const entry = readEntry()
  if (!entry || entry.wallet !== wallet) return null
  if (entry.savedAt > 0 && Date.now() - entry.savedAt > MAX_AGE_MS) {
    clearActiveMvMBattle()
    return null
  }
  return entry
}

/**
 * Drops the stored battle. Pass `battleId` to make the clear conditional — a late
 * `mvm:battle-ended` for an older battle must not wipe the entry for the one just started.
 */
export function clearActiveMvMBattle(battleId?: string | null): void {
  if (typeof window === 'undefined') return
  const id = String(battleId || '').trim()
  if (id) {
    const entry = readEntry()
    if (entry && entry.battleId !== id) return
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nothing to do */
  }
}
